import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

import { OpenAPI } from "../../../client"

export const Route = createFileRoute("/_layout/admin/marketing-campaigns")({
  component: MarketingCampaignsAdminPage,
})

type TemplateItem = {
  id: string
  name: string
  slug: string
  status: string
  supported_variables?: string[] | null
}

type LeadItem = {
  id: string
  email: string
  first_name: string
  last_name: string
  company_name: string
  status: string
  unsubscribed: boolean
}

type CampaignItem = {
  id: string
  name: string
  status: string
  template_id: string
  scheduled_at?: string | null
  started_at?: string | null
  completed_at?: string | null
  selection_mode: string
  filters: Record<string, unknown> | null
  tracked_links?: Record<string, string> | null
  throttle_per_minute: number
  daily_plan?: CampaignDailyPlanEntry[] | null
  send_window_start?: string | null
  send_window_end?: string | null
  schedule_timezone?: string | null
  recipient_count?: number
  clicked_recipient_count?: number
  total_clicks?: number
  ctr?: number
  created_at: string
  updated_at: string
}

type CampaignListResponse = {
  items: CampaignItem[]
  total: number
  limit: number
  offset: number
}

type CampaignDetailResponse = {
  campaign: CampaignItem
  template?: TemplateItem | null
  recipient_stats: Record<string, number>
  click_stats: Record<string, number>
  recipient_count: number
}

type RecipientItem = {
  id: string
  campaign_id: string
  lead_id: string
  email: string
  rendered_subject: string
  delivery_status: string
  provider_message_id?: string | null
  failure_reason?: string | null
  sent_at?: string | null
  planned_send_at?: string | null
  first_clicked_at?: string | null
  last_clicked_at?: string | null
  click_count: number
  created_at: string
}

type RecipientsResponse = {
  items: RecipientItem[]
  total: number
  limit: number
  offset: number
}

type ClickEventItem = {
  id: string
  lead_id: string
  lead_email: string
  campaign_id: string
  recipient_id: string
  recipient_email: string
  click_token_id: string
  target_url: string
  ip_address: string
  user_agent: string
  referrer: string
  clicked_at: string
  created_at: string
}

type ClickEventsResponse = {
  items: ClickEventItem[]
  total: number
  limit: number
  offset: number
}

type CampaignDailyPlanEntry = {
  date: string
  limit: number
}

type GenericListResponse<T> = {
  items: T[]
  total: number
  limit: number
  offset: number
}

type CampaignPayload = {
  name: string
  template_id: string
  selection_mode: string
  filters: Record<string, unknown>
  tracked_links: Record<string, string>
  scheduled_at?: string
  throttle_per_minute: number
  daily_plan: CampaignDailyPlanEntry[]
  send_window_start: string
  send_window_end: string
  schedule_timezone: string
}

const defaultCampaignForm: CampaignPayload = {
  name: "",
  template_id: "",
  selection_mode: "manual",
  filters: { lead_ids: [] },
  tracked_links: {},
  scheduled_at: "",
  throttle_per_minute: 60,
  daily_plan: [],
  send_window_start: "09:00",
  send_window_end: "17:00",
  schedule_timezone: "Europe/Warsaw",
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("access_token") || ""
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(
        parsed?.error ||
          parsed?.detail ||
          parsed?.message ||
          `HTTP ${res.status}`,
      )
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function getCampaignStatusColor(status?: string) {
  switch (status) {
    case "draft":
      return "gray"
    case "scheduled":
      return "blue"
    case "processing":
      return "orange"
    case "completed":
      return "green"
    case "paused":
      return "yellow"
    case "cancelled":
      return "red"
    default:
      return "gray"
  }
}

function getRecipientStatusColor(status?: string) {
  switch (status) {
    case "pending":
      return "gray"
    case "queued":
      return "blue"
    case "sent":
      return "green"
    case "delivered":
      return "green"
    case "failed":
      return "red"
    case "skipped":
      return "yellow"
    case "unsubscribed":
      return "orange"
    default:
      return "gray"
  }
}

function getCampaignStatusLabel(status?: string) {
  switch (status) {
    case "draft":
      return "Szkic"
    case "scheduled":
      return "Zaplanowana"
    case "processing":
      return "W trakcie"
    case "completed":
      return "Zakończona"
    case "paused":
      return "Wstrzymana"
    case "cancelled":
      return "Anulowana"
    default:
      return status || "Nieznany"
  }
}

function getRecipientStatusLabel(status?: string) {
  switch (status) {
    case "pending":
      return "Oczekuje"
    case "queued":
      return "W kolejce"
    case "sent":
      return "Wysłano"
    case "delivered":
      return "Dostarczono"
    case "failed":
      return "Błąd"
    case "skipped":
      return "Pominięto"
    case "unsubscribed":
      return "Wypisany"
    default:
      return status || "Nieznany"
  }
}

function getClickSegmentLabel(value: string) {
  switch (value) {
    case "clicked":
      return "Kliknęli w wiadomość"
    case "not_clicked":
      return "Bez kliknięcia"
    default:
      return "Wszystkie"
  }
}

function getCampaignActionLabel(action?: string) {
  switch (action) {
    case "schedule":
      return "zaplanowana"
    case "start":
      return "uruchomiona"
    case "refresh":
      return "odświeżona"
    case "pause":
      return "wstrzymana"
    case "cancel":
      return "anulowana"
    default:
      return action || "wykonana"
  }
}

function toDateTimeLocalInput(value?: string | null) {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const pad = (num: number) => String(num).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toApiDateTime(value?: string) {
  const normalized = value?.trim()
  if (!normalized) return undefined

  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return undefined

  return date.toISOString()
}

function serializeDailyPlan(entries?: CampaignDailyPlanEntry[] | null) {
  return (entries ?? []).map((item) => `${item.date}:${item.limit}`).join("\n")
}

function parseDailyPlanInput(value: string) {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [datePart, limitPart] = line.split(":")
      return {
        date: (datePart || "").trim(),
        limit: Number((limitPart || "").trim()),
      }
    })
}

function serializeTrackedLinks(links?: Record<string, string> | null) {
  return Object.entries(links ?? {})
    .map(([key, value]) => `${key}=${value}`)
    .join("\n")
}

function parseTrackedLinksInput(value: string) {
  return Object.fromEntries(
    value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const separatorIndex = line.indexOf("=")
        if (separatorIndex < 0) {
          return [line.trim(), ""]
        }
        return [
          line.slice(0, separatorIndex).trim(),
          line.slice(separatorIndex + 1).trim(),
        ]
      })
      .filter(([key, url]) => key && url),
  )
}

function formatMobileDateTime(value?: string | null) {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return `${date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  })} ${date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
}

function MarketingCampaignsAdminPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false
  const panelBg = useColorModeValue("white", "gray.800")
  const mutedTextColor = useColorModeValue("gray.500", "gray.300")
  const tableHeadBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")

  const [q, setQ] = useState("")
  const [status, setStatus] = useState("")
  const [sortBy, setSortBy] = useState("")
  const [editingId, setEditingId] = useState<string>("")
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("")
  const [clickedFilter, setClickedFilter] = useState("")
  const [clickSearch, setClickSearch] = useState("")
  const [clickDateFrom, setClickDateFrom] = useState("")
  const [clickDateTo, setClickDateTo] = useState("")
  const [manualLeadIdsInput, setManualLeadIdsInput] = useState("")
  const [tagFilterInput, setTagFilterInput] = useState("")
  const [dailyPlanInput, setDailyPlanInput] = useState("")
  const [trackedLinksInput, setTrackedLinksInput] = useState("")
  const [form, setForm] = useState<CampaignPayload>(defaultCampaignForm)

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    params.set("limit", "200")
    if (q.trim()) params.set("q", q.trim())
    if (status) params.set("status", status)
    if (sortBy) params.set("sort", sortBy)
    return params.toString()
  }, [q, status, sortBy])

  const campaignsQuery = useQuery({
    queryKey: ["lms", "marketing", "campaigns", queryString],
    queryFn: () =>
      apiFetch<CampaignListResponse>(
        `/v1/lms/marketing/campaigns?${queryString}`,
      ),
  })

  const templatesQuery = useQuery({
    queryKey: ["lms", "marketing", "templates", "options"],
    queryFn: () =>
      apiFetch<GenericListResponse<TemplateItem>>(
        "/v1/lms/marketing/templates?limit=200&status=active",
      ),
  })

  const leadsQuery = useQuery({
    queryKey: ["lms", "marketing", "leads", "options"],
    queryFn: () =>
      apiFetch<GenericListResponse<LeadItem>>(
        "/v1/lms/marketing/leads?limit=200&status=active&unsubscribed=false",
      ),
  })

  const detailQuery = useQuery({
    queryKey: ["lms", "marketing", "campaign", selectedCampaignId],
    queryFn: () =>
      apiFetch<CampaignDetailResponse>(
        `/v1/lms/marketing/campaigns/${selectedCampaignId}`,
      ),
    enabled: !!selectedCampaignId,
  })

  const recipientsQuery = useQuery({
    queryKey: [
      "lms",
      "marketing",
      "campaign",
      selectedCampaignId,
      "recipients",
      clickedFilter,
    ],
    queryFn: () =>
      apiFetch<RecipientsResponse>(
        `/v1/lms/marketing/campaigns/${selectedCampaignId}/recipients?limit=200${clickedFilter ? `&clicked=${clickedFilter === "clicked" ? "true" : "false"}` : ""}`,
      ),
    enabled: !!selectedCampaignId,
  })

  const clickEventsQuery = useQuery({
    queryKey: [
      "lms",
      "marketing",
      "campaign",
      selectedCampaignId,
      "click-events",
      clickSearch,
      clickDateFrom,
      clickDateTo,
    ],
    queryFn: () =>
      apiFetch<ClickEventsResponse>(
        `/v1/lms/marketing/campaigns/${selectedCampaignId}/click-events?limit=200${clickSearch.trim() ? `&q=${encodeURIComponent(clickSearch.trim())}` : ""}${clickDateFrom ? `&date_from=${encodeURIComponent(clickDateFrom)}` : ""}${clickDateTo ? `&date_to=${encodeURIComponent(clickDateTo)}` : ""}`,
      ),
    enabled: !!selectedCampaignId,
  })

  useEffect(() => {
    if (!selectedCampaignId && campaignsQuery.data?.items?.length) {
      setSelectedCampaignId(campaignsQuery.data.items[0].id)
    }
  }, [campaignsQuery.data?.items, selectedCampaignId])

  const processingCount = useMemo(
    () =>
      (campaignsQuery.data?.items ?? []).filter(
        (item) => item.status === "processing",
      ).length,
    [campaignsQuery.data?.items],
  )
  const scheduledCount = useMemo(
    () =>
      (campaignsQuery.data?.items ?? []).filter(
        (item) => item.status === "scheduled",
      ).length,
    [campaignsQuery.data?.items],
  )

  const invalidateCampaignQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ["lms", "marketing", "campaigns"],
    })
    queryClient.invalidateQueries({
      queryKey: ["lms", "marketing", "campaign"],
    })
  }

  const upsertMutation = useMutation({
    mutationFn: (payload: CampaignPayload) =>
      apiFetch<CampaignItem>(
        editingId
          ? `/v1/lms/marketing/campaigns/${editingId}`
          : "/v1/lms/marketing/campaigns",
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: (item) => {
      toast({
        title: editingId
          ? "Kampania została zaktualizowana"
          : "Kampania została utworzona",
        status: "success",
        duration: 2500,
      })
      invalidateCampaignQueries()
      setSelectedCampaignId(item.id)
      startCreate()
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się zapisać",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (campaignId: string) =>
      apiFetch<{ status: string }>(
        `/v1/lms/marketing/campaigns/${campaignId}`,
        {
          method: "DELETE",
        },
      ),
    onSuccess: () => {
      toast({
        title: "Kampania została usunięta",
        status: "warning",
        duration: 2500,
      })
      invalidateCampaignQueries()
      setSelectedCampaignId("")
      if (editingId) startCreate()
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się usunąć",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const actionMutation = useMutation({
    mutationFn: ({
      campaignId,
      action,
    }: {
      campaignId: string
      action: string
    }) =>
      apiFetch<any>(`/v1/lms/marketing/campaigns/${campaignId}/${action}`, {
        method: "POST",
      }),
    onSuccess: (_, variables) => {
      toast({
        title: `Kampania ${getCampaignActionLabel(variables.action)}`,
        status: "success",
        duration: 2500,
      })
      invalidateCampaignQueries()
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się wykonać akcji",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  function startCreate() {
    setEditingId("")
    setManualLeadIdsInput("")
    setTagFilterInput("")
    setDailyPlanInput("")
    setTrackedLinksInput("")
    setForm(defaultCampaignForm)
  }

  function startEdit(item: CampaignItem) {
    const filters = (item.filters ?? {}) as Record<string, unknown>
    const leadIDs = Array.isArray(filters.lead_ids)
      ? filters.lead_ids.map((value) => String(value))
      : []
    const tag = typeof filters.tag === "string" ? filters.tag : ""

    setEditingId(item.id)
    setSelectedCampaignId(item.id)
    setManualLeadIdsInput(leadIDs.join(", "))
    setTagFilterInput(tag)
    setDailyPlanInput(serializeDailyPlan(item.daily_plan))
    setTrackedLinksInput(serializeTrackedLinks(item.tracked_links))
    setForm({
      name: item.name,
      template_id: item.template_id,
      selection_mode: item.selection_mode || "manual",
      filters,
      tracked_links: item.tracked_links ?? {},
      scheduled_at: toDateTimeLocalInput(item.scheduled_at),
      throttle_per_minute: item.throttle_per_minute || 60,
      daily_plan: item.daily_plan ?? [],
      send_window_start: item.send_window_start || "09:00",
      send_window_end: item.send_window_end || "17:00",
      schedule_timezone: item.schedule_timezone || "Europe/Warsaw",
    })
  }

  const templateOptions = templatesQuery.data?.items ?? []
  const selectedTemplate = templateOptions.find(
    (item) => item.id === form.template_id,
  )
  const selectedTemplateVariables = selectedTemplate?.supported_variables ?? []
  const leadOptions = leadsQuery.data?.items ?? []
  const recipientStats = detailQuery.data?.recipient_stats ?? {}
  const clickStats = detailQuery.data?.click_stats ?? {}

  const renderCampaignActions = (
    item: CampaignItem,
    size: "xs" | "sm" = "xs",
    stacked = false,
  ) => {
    const canRefreshSnapshot =
      item.status === "scheduled" ||
      item.status === "processing" ||
      item.status === "paused"

    return (
      <Grid
        templateColumns={
          stacked
            ? "repeat(2, minmax(0, 1fr))"
            : "repeat(auto-fit, minmax(120px, max-content))"
        }
        justifyContent={stacked ? "stretch" : "end"}
        gap={2}
        w="full"
      >
        <Button size={size} variant="outline" onClick={() => startEdit(item)}>
          Edytuj
        </Button>
        <Button
          size={size}
          colorScheme="blue"
          variant="outline"
          onClick={() =>
            actionMutation.mutate({
              campaignId: item.id,
              action: "schedule",
            })
          }
          isLoading={
            actionMutation.isPending &&
            actionMutation.variables?.campaignId === item.id &&
            actionMutation.variables?.action === "schedule"
          }
        >
          Zaplanuj
        </Button>
        <Button
          size={size}
          colorScheme="green"
          variant="outline"
          onClick={() =>
            actionMutation.mutate({
              campaignId: item.id,
              action: "start",
            })
          }
          isLoading={
            actionMutation.isPending &&
            actionMutation.variables?.campaignId === item.id &&
            actionMutation.variables?.action === "start"
          }
        >
          Uruchom
        </Button>
        {canRefreshSnapshot ? (
          <Button
            size={size}
            colorScheme="cyan"
            variant="outline"
            h="auto"
            minH={10}
            px={4}
            py={2}
            whiteSpace="normal"
            textAlign="center"
            onClick={() =>
              actionMutation.mutate({
                campaignId: item.id,
                action: "refresh",
              })
            }
            isLoading={
              actionMutation.isPending &&
              actionMutation.variables?.campaignId === item.id &&
              actionMutation.variables?.action === "refresh"
            }
          >
            Odśwież snapshot
          </Button>
        ) : null}
        <Button
          size={size}
          colorScheme="yellow"
          variant="outline"
          onClick={() =>
            actionMutation.mutate({
              campaignId: item.id,
              action: "pause",
            })
          }
          isLoading={
            actionMutation.isPending &&
            actionMutation.variables?.campaignId === item.id &&
            actionMutation.variables?.action === "pause"
          }
        >
          Wstrzymaj
        </Button>
        <Button
          size={size}
          colorScheme="orange"
          variant="outline"
          onClick={() =>
            actionMutation.mutate({
              campaignId: item.id,
              action: "cancel",
            })
          }
          isLoading={
            actionMutation.isPending &&
            actionMutation.variables?.campaignId === item.id &&
            actionMutation.variables?.action === "cancel"
          }
        >
          Anuluj
        </Button>
        <Button
          size={size}
          colorScheme="red"
          variant="ghost"
          onClick={() => deleteMutation.mutate(item.id)}
          isLoading={
            deleteMutation.isPending && deleteMutation.variables === item.id
          }
        >
          Usuń
        </Button>
      </Grid>
    )
  }

  return (
    <Container maxW="9xl" py={{ base: 3, md: 4 }} px={{ base: 3, md: 4 }}>
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
          <Heading size="lg">Kampanie</Heading>
          <Text color={mutedTextColor} mt={1}>
            Planowanie kampanii cold e-mail, wybór odbiorców, zarządzanie cyklem
            życia i snapshotem odbiorców.
          </Text>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={3}
            mt={4}
          >
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Załadowane kampanie</StatLabel>
                <StatNumber>{campaignsQuery.data?.total ?? 0}</StatNumber>
                <StatHelpText>W zakresie bieżącego filtra</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Zaplanowane</StatLabel>
                <StatNumber
                  color={scheduledCount > 0 ? "blue.500" : "gray.700"}
                >
                  {scheduledCount}
                </StatNumber>
                <StatHelpText>Oczekują na harmonogram</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>W trakcie</StatLabel>
                <StatNumber
                  color={processingCount > 0 ? "orange.500" : "gray.700"}
                >
                  {processingCount}
                </StatNumber>
                <StatHelpText>Wysyłka trwa</StatHelpText>
              </Stat>
            </Box>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
            <GridItem>
              <FormControl>
                <FormLabel>Szukaj</FormLabel>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="nazwa kampanii"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="draft">Szkic</option>
                  <option value="scheduled">Zaplanowana</option>
                  <option value="processing">W trakcie</option>
                  <option value="completed">Zakończona</option>
                  <option value="paused">Wstrzymana</option>
                  <option value="cancelled">Anulowana</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Sortowanie</FormLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="">Domyślne</option>
                  <option value="engagement_desc">
                    Najwyższe zaangażowanie
                  </option>
                  <option value="engagement_asc">
                    Najniższe zaangażowanie
                  </option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, md: 1 }} alignSelf="end">
              <HStack>
                <Button flex="1" onClick={startCreate}>
                  Nowa kampania
                </Button>
                <Button
                  flex="1"
                  variant="outline"
                  isDisabled={!selectedCampaignId}
                  onClick={() =>
                    selectedCampaignId &&
                    startEdit(
                      (campaignsQuery.data?.items ?? []).find(
                        (item) => item.id === selectedCampaignId,
                      )!,
                    )
                  }
                >
                  Edytuj wybraną
                </Button>
              </HStack>
            </GridItem>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Heading size="md" mb={4}>
            {editingId ? "Edytuj kampanię" : "Utwórz kampanię"}
          </Heading>
          <Grid
            templateColumns={{ base: "1fr", lg: "repeat(12, 1fr)" }}
            gap={3}
          >
            <GridItem colSpan={{ base: 12, lg: 4 }}>
              <FormControl isRequired>
                <FormLabel>Nazwa</FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm((current) => ({ ...current, name: e.target.value }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 4 }}>
              <FormControl isRequired>
                <FormLabel>Szablon</FormLabel>
                <Select
                  value={form.template_id}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      template_id: e.target.value,
                    }))
                  }
                >
                  <option value="">Wybierz szablon</option>
                  {templateOptions.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} ({item.slug})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 4 }}>
              <FormControl>
                <FormLabel>Tryb wyboru</FormLabel>
                <Select
                  value={form.selection_mode}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      selection_mode: e.target.value,
                    }))
                  }
                >
                  <option value="manual">Wybór ręczny</option>
                  <option value="all_active">Wszyscy aktywni</option>
                  <option value="tag_filter">Filtr po tagu</option>
                </Select>
              </FormControl>
            </GridItem>

            <GridItem colSpan={{ base: 12, lg: 6 }}>
              <FormControl>
                <FormLabel>Zaplanowano na</FormLabel>
                <Input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      scheduled_at: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 6 }}>
              <FormControl>
                <FormLabel>Limit techniczny na minutę</FormLabel>
                <Input
                  type="number"
                  min={1}
                  value={String(form.throttle_per_minute)}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      throttle_per_minute: Number(e.target.value || 60),
                    }))
                  }
                />
                <Text mt={2} fontSize="sm" color={mutedTextColor}>
                  Dodatkowy limit dla workera wysyłki po zastosowaniu planu
                  dziennego i losowego rozkładu.
                </Text>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 4 }}>
              <FormControl>
                <FormLabel>Początek okna</FormLabel>
                <Input
                  type="time"
                  value={form.send_window_start}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      send_window_start: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 4 }}>
              <FormControl>
                <FormLabel>Koniec okna</FormLabel>
                <Input
                  type="time"
                  value={form.send_window_end}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      send_window_end: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, lg: 4 }}>
              <FormControl>
                <FormLabel>Strefa czasowa</FormLabel>
                <Input
                  value={form.schedule_timezone}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      schedule_timezone: e.target.value,
                    }))
                  }
                  placeholder="Europe/Warsaw"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={12}>
              <FormControl>
                <FormLabel>Plan wysyłki według dni</FormLabel>
                <Textarea
                  rows={5}
                  value={dailyPlanInput}
                  onChange={(e) => setDailyPlanInput(e.target.value)}
                  placeholder={
                    "2026-03-29:90\n2026-03-30:120\n2026-03-31:150\n2026-04-01:140"
                  }
                />
                <Text mt={2} fontSize="sm" color={mutedTextColor}>
                  Jeden wiersz = jeden dzień w formacie YYYY-MM-DD:liczba.
                  Wiadomości w danym dniu zostaną losowo rozłożone w obrębie
                  okna.
                </Text>
              </FormControl>
            </GridItem>

            <GridItem colSpan={12}>
              <FormControl>
                <FormLabel>Śledzone linki</FormLabel>
                <Textarea
                  rows={5}
                  value={trackedLinksInput}
                  onChange={(e) => setTrackedLinksInput(e.target.value)}
                  placeholder={
                    "demo_url=https://example.com/demo\npricing_url=https://example.com/pricing\ncontact_url=https://example.com/contact"
                  }
                />
                <Text mt={2} fontSize="sm" color={mutedTextColor}>
                  {
                    "Jeden wiersz = `placeholder_url=https://docelowy-adres`. W szablonie użyj potem `{{placeholder_url}}`, np. `{{demo_url}}`. Każdy wpis dostanie osobny tracked redirect."
                  }
                </Text>
                {selectedTemplateVariables.length > 0 ? (
                  <Text mt={1} fontSize="sm" color={mutedTextColor}>
                    Zmienne w wybranym szablonie:{" "}
                    {selectedTemplateVariables.join(", ")}
                  </Text>
                ) : null}
              </FormControl>
            </GridItem>

            {form.selection_mode === "manual" && (
              <GridItem colSpan={12}>
                <FormControl>
                  <FormLabel>Ręczne ID leadów</FormLabel>
                  <Textarea
                    rows={3}
                    value={manualLeadIdsInput}
                    onChange={(e) => setManualLeadIdsInput(e.target.value)}
                    placeholder="uuid-1, uuid-2, uuid-3"
                  />
                  <Text mt={2} fontSize="sm" color={mutedTextColor}>
                    Dostępne aktywne leady:{" "}
                    {leadOptions
                      .slice(0, 8)
                      .map((item) => `${item.id} (${item.email})`)
                      .join(" | ") || "—"}
                  </Text>
                </FormControl>
              </GridItem>
            )}

            {form.selection_mode === "tag_filter" && (
              <GridItem colSpan={12}>
                <FormControl>
                  <FormLabel>Filtr po tagu</FormLabel>
                  <Input
                    value={tagFilterInput}
                    onChange={(e) => setTagFilterInput(e.target.value)}
                    placeholder="enterprise, logistics, ecommerce"
                  />
                </FormControl>
              </GridItem>
            )}

            <GridItem colSpan={12}>
              <HStack justify="flex-end">
                <Button variant="ghost" onClick={startCreate}>
                  Resetuj
                </Button>
                <Button
                  colorScheme="green"
                  isLoading={upsertMutation.isPending}
                  onClick={() => {
                    const dailyPlan = parseDailyPlanInput(dailyPlanInput)
                    const trackedLinks =
                      parseTrackedLinksInput(trackedLinksInput)
                    const filters =
                      form.selection_mode === "manual"
                        ? {
                            lead_ids: manualLeadIdsInput
                              .split(",")
                              .map((value) => value.trim())
                              .filter(Boolean),
                          }
                        : form.selection_mode === "tag_filter"
                          ? { tag: tagFilterInput.trim() }
                          : {}

                    upsertMutation.mutate({
                      ...form,
                      scheduled_at: toApiDateTime(form.scheduled_at),
                      daily_plan: dailyPlan,
                      filters,
                      tracked_links: trackedLinks,
                    })
                  }}
                >
                  {editingId ? "Zaktualizuj kampanię" : "Utwórz kampanię"}
                </Button>
              </HStack>
            </GridItem>
          </Grid>
        </Box>

        <Grid
          templateColumns={{ base: "1fr", xl: "minmax(0,1fr) minmax(0,1fr)" }}
          gap={4}
          alignItems="start"
        >
          <Box
            borderWidth="1px"
            borderRadius="xl"
            p={{ base: 3, md: 4 }}
            minW={0}
          >
            <Heading size="md" mb={4}>
              Lista kampanii
            </Heading>
            {campaignsQuery.isPending ? (
              <Spinner />
            ) : isMobile ? (
              <VStack align="stretch" spacing={3}>
                {(campaignsQuery.data?.items ?? []).map((item) => (
                  <Box
                    key={item.id}
                    borderWidth="1px"
                    borderRadius="lg"
                    p={3}
                    bg={
                      selectedCampaignId === item.id
                        ? tableRowHoverBg
                        : "transparent"
                    }
                  >
                    <VStack align="stretch" spacing={3}>
                      <Box minW={0}>
                        <Button
                          size="sm"
                          variant="link"
                          onClick={() => setSelectedCampaignId(item.id)}
                          whiteSpace="normal"
                          textAlign="left"
                          maxW="full"
                          justifyContent="flex-start"
                        >
                          {item.name}
                        </Button>
                        <Text fontSize="sm" color={mutedTextColor} mt={1}>
                          {(
                            templateOptions.find(
                              (template) => template.id === item.template_id,
                            )?.name || item.template_id
                          ).slice(0, 48)}
                        </Text>
                      </Box>
                      <HStack justify="space-between" align="center">
                        <Badge
                          colorScheme={getCampaignStatusColor(item.status)}
                        >
                          {getCampaignStatusLabel(item.status)}
                        </Badge>
                        <Text
                          fontSize="xs"
                          color={mutedTextColor}
                          textAlign="right"
                        >
                          {formatMobileDateTime(item.scheduled_at)}
                        </Text>
                      </HStack>
                      <Grid
                        templateColumns="repeat(2, minmax(0, 1fr))"
                        gap={3}
                        mt={3}
                      >
                        <Box>
                          <Text fontSize="xs" color={mutedTextColor}>
                            CTR
                          </Text>
                          <Text fontWeight="semibold">
                            {typeof item.ctr === "number"
                              ? `${item.ctr.toFixed(1)}%`
                              : "0.0%"}
                          </Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color={mutedTextColor}>
                            Kliknęli
                          </Text>
                          <Text fontWeight="semibold">
                            {item.clicked_recipient_count ?? 0}
                            {item.recipient_count
                              ? ` / ${item.recipient_count}`
                              : ""}
                          </Text>
                        </Box>
                        <Box gridColumn="1 / -1">
                          <Text fontSize="xs" color={mutedTextColor}>
                            Szablon
                          </Text>
                          <Text fontWeight="medium" noOfLines={2}>
                            {templateOptions.find(
                              (template) => template.id === item.template_id,
                            )?.name || item.template_id}
                          </Text>
                        </Box>
                      </Grid>
                      <Box mt={3}>
                        {renderCampaignActions(item, "sm", true)}
                      </Box>
                    </VStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <TableContainer overflowX="auto">
                <Table size="sm">
                  <Thead bg={tableHeadBg}>
                    <Tr>
                      <Th>Nazwa</Th>
                      <Th>Status</Th>
                      <Th>Szablon</Th>
                      <Th>CTR</Th>
                      <Th>Kliknęli</Th>
                      <Th>Zaplanowano</Th>
                      <Th textAlign="right">Akcje</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(campaignsQuery.data?.items ?? []).map((item) => (
                      <Tr
                        key={item.id}
                        _hover={{ bg: tableRowHoverBg }}
                        bg={
                          selectedCampaignId === item.id
                            ? tableRowHoverBg
                            : "transparent"
                        }
                      >
                        <Td>
                          <Button
                            size="xs"
                            variant="link"
                            onClick={() => setSelectedCampaignId(item.id)}
                          >
                            {item.name}
                          </Button>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={getCampaignStatusColor(item.status)}
                          >
                            {getCampaignStatusLabel(item.status)}
                          </Badge>
                        </Td>
                        <Td>
                          {templateOptions.find(
                            (template) => template.id === item.template_id,
                          )?.name || item.template_id}
                        </Td>
                        <Td>
                          {typeof item.ctr === "number"
                            ? `${item.ctr.toFixed(1)}%`
                            : "0.0%"}
                        </Td>
                        <Td>
                          {item.clicked_recipient_count ?? 0}
                          {item.recipient_count
                            ? ` / ${item.recipient_count}`
                            : ""}
                        </Td>
                        <Td>
                          {item.scheduled_at
                            ? new Date(item.scheduled_at).toLocaleString()
                            : "—"}
                        </Td>
                        <Td>{renderCampaignActions(item)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="xl"
            p={{ base: 3, md: 4 }}
            minW={0}
          >
            <Heading size="md" mb={4}>
              Szczegóły kampanii
            </Heading>
            {!selectedCampaignId ? (
              <Text color={mutedTextColor}>
                Wybierz kampanię, aby zobaczyć statystyki i odbiorców.
              </Text>
            ) : detailQuery.isPending ? (
              <Spinner />
            ) : (
              <VStack align="stretch" spacing={4}>
                <Box borderWidth="1px" borderRadius="lg" p={3}>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Kampania
                  </Text>
                  <Text fontWeight="semibold">
                    {detailQuery.data?.campaign.name}
                  </Text>
                  <Text fontSize="sm" color={mutedTextColor} mt={1}>
                    Szablon: {detailQuery.data?.template?.name || "—"}
                  </Text>
                </Box>

                <Grid
                  templateColumns={{
                    base: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  }}
                  gap={3}
                >
                  <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <Stat>
                      <StatLabel>Odbiorcy</StatLabel>
                      <StatNumber>
                        {detailQuery.data?.recipient_count ?? 0}
                      </StatNumber>
                      <StatHelpText>Łączny rozmiar migawki</StatHelpText>
                    </Stat>
                  </Box>
                  <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <Stat>
                      <StatLabel>Kliknęli</StatLabel>
                      <StatNumber
                        color={
                          (clickStats.clicked ?? 0) > 0
                            ? "blue.500"
                            : "gray.700"
                        }
                      >
                        {clickStats.clicked ?? 0}
                      </StatNumber>
                      <StatHelpText>
                        Odbiorcy z co najmniej jednym kliknięciem
                      </StatHelpText>
                    </Stat>
                  </Box>
                  <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <Stat>
                      <StatLabel>Łącznie kliknięć</StatLabel>
                      <StatNumber
                        color={
                          (clickStats.total_clicks ?? 0) > 0
                            ? "green.500"
                            : "gray.700"
                        }
                      >
                        {clickStats.total_clicks ?? 0}
                      </StatNumber>
                      <StatHelpText>Łączne przejścia z wiadomości</StatHelpText>
                    </Stat>
                  </Box>
                  {Object.entries(recipientStats).map(([key, value]) => (
                    <Box key={key} borderWidth="1px" borderRadius="lg" p={3}>
                      <Stat>
                        <StatLabel>{getRecipientStatusLabel(key)}</StatLabel>
                        <StatNumber>{value}</StatNumber>
                        <StatHelpText>Grupa statusu</StatHelpText>
                      </Stat>
                    </Box>
                  ))}
                </Grid>

                <Box borderWidth="1px" borderRadius="lg" p={3}>
                  <HStack
                    justify="space-between"
                    mb={3}
                    align={{ base: "stretch", md: "end" }}
                    flexDir={{ base: "column", md: "row" }}
                  >
                    <Text fontSize="sm" color={mutedTextColor}>
                      Migawka odbiorców
                    </Text>
                    <FormControl maxW={{ base: "full", md: "260px" }}>
                      <FormLabel fontSize="xs" mb={1}>
                        Segment
                      </FormLabel>
                      <Select
                        size="sm"
                        value={clickedFilter}
                        onChange={(e) => setClickedFilter(e.target.value)}
                      >
                        <option value="">Wszyscy</option>
                        <option value="clicked">
                          {getClickSegmentLabel("clicked")}
                        </option>
                        <option value="not_clicked">
                          {getClickSegmentLabel("not_clicked")}
                        </option>
                      </Select>
                    </FormControl>
                  </HStack>
                  {recipientsQuery.isPending ? (
                    <Spinner />
                  ) : isMobile ? (
                    <VStack align="stretch" spacing={3}>
                      {(recipientsQuery.data?.items ?? []).map((item) => (
                        <Box
                          key={item.id}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                        >
                          <HStack justify="space-between" align="start">
                            <Text fontWeight="semibold" wordBreak="break-word">
                              {item.email}
                            </Text>
                            <Badge
                              colorScheme={getRecipientStatusColor(
                                item.delivery_status,
                              )}
                            >
                              {getRecipientStatusLabel(item.delivery_status)}
                            </Badge>
                          </HStack>
                          <Grid
                            templateColumns="repeat(2, minmax(0, 1fr))"
                            gap={3}
                            mt={3}
                          >
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                Zaplanowano
                              </Text>
                              <Text fontSize="sm">
                                {item.planned_send_at
                                  ? new Date(
                                      item.planned_send_at,
                                    ).toLocaleString()
                                  : "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                Wysłano
                              </Text>
                              <Text fontSize="sm">
                                {item.sent_at
                                  ? new Date(item.sent_at).toLocaleString()
                                  : "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                Kliknięcia
                              </Text>
                              <Text fontSize="sm">{item.click_count}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                Ostatnie kliknięcie
                              </Text>
                              <Text fontSize="sm">
                                {item.last_clicked_at
                                  ? new Date(
                                      item.last_clicked_at,
                                    ).toLocaleString()
                                  : "—"}
                              </Text>
                            </Box>
                            <Box gridColumn="1 / -1">
                              <Text fontSize="xs" color={mutedTextColor}>
                                Błąd
                              </Text>
                              <Text fontSize="sm" wordBreak="break-word">
                                {item.failure_reason || "—"}
                              </Text>
                            </Box>
                          </Grid>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <TableContainer
                      maxH="420px"
                      overflowY="auto"
                      overflowX="auto"
                    >
                      <Table size="sm">
                        <Thead
                          bg={tableHeadBg}
                          position="sticky"
                          top={0}
                          zIndex={1}
                        >
                          <Tr>
                            <Th>Email</Th>
                            <Th>Status</Th>
                            <Th>Zaplanowano</Th>
                            <Th>Kliknięcia</Th>
                            <Th>Ostatnie kliknięcie</Th>
                            <Th>Wysłano</Th>
                            <Th>Błąd</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(recipientsQuery.data?.items ?? []).map((item) => (
                            <Tr key={item.id}>
                              <Td>{item.email}</Td>
                              <Td>
                                <Badge
                                  colorScheme={getRecipientStatusColor(
                                    item.delivery_status,
                                  )}
                                >
                                  {getRecipientStatusLabel(
                                    item.delivery_status,
                                  )}
                                </Badge>
                              </Td>
                              <Td>
                                {item.planned_send_at
                                  ? new Date(
                                      item.planned_send_at,
                                    ).toLocaleString()
                                  : "—"}
                              </Td>
                              <Td>
                                {item.click_count > 0 ? (
                                  <Badge colorScheme="blue">
                                    {item.click_count}
                                  </Badge>
                                ) : (
                                  <Text color={mutedTextColor}>0</Text>
                                )}
                              </Td>
                              <Td>
                                {item.last_clicked_at
                                  ? new Date(
                                      item.last_clicked_at,
                                    ).toLocaleString()
                                  : "—"}
                              </Td>
                              <Td>
                                {item.sent_at
                                  ? new Date(item.sent_at).toLocaleString()
                                  : "—"}
                              </Td>
                              <Td maxW="240px">
                                <Text noOfLines={2}>
                                  {item.failure_reason || "—"}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>

                <Box borderWidth="1px" borderRadius="lg" p={3}>
                  <HStack
                    justify="space-between"
                    mb={3}
                    align={{ base: "stretch", md: "end" }}
                    flexDir={{ base: "column", md: "row" }}
                  >
                    <Text fontSize="sm" color={mutedTextColor}>
                      Zdarzenia kliknięć
                    </Text>
                    <HStack
                      align="end"
                      flexDir={{ base: "column", md: "row" }}
                      w={{ base: "full", md: "auto" }}
                    >
                      <FormControl maxW={{ base: "full", md: "180px" }}>
                        <FormLabel fontSize="xs" mb={1}>
                          Od
                        </FormLabel>
                        <Input
                          size="sm"
                          type="date"
                          value={clickDateFrom}
                          onChange={(e) => setClickDateFrom(e.target.value)}
                        />
                      </FormControl>
                      <FormControl maxW={{ base: "full", md: "180px" }}>
                        <FormLabel fontSize="xs" mb={1}>
                          Do
                        </FormLabel>
                        <Input
                          size="sm"
                          type="date"
                          value={clickDateTo}
                          onChange={(e) => setClickDateTo(e.target.value)}
                        />
                      </FormControl>
                      <FormControl maxW={{ base: "full", md: "320px" }}>
                        <FormLabel fontSize="xs" mb={1}>
                          Szukaj w zdarzeniach
                        </FormLabel>
                        <Input
                          size="sm"
                          value={clickSearch}
                          onChange={(e) => setClickSearch(e.target.value)}
                          placeholder="email / URL / IP / referrer"
                        />
                      </FormControl>
                    </HStack>
                  </HStack>
                  {clickEventsQuery.isPending ? (
                    <Spinner />
                  ) : isMobile ? (
                    <VStack align="stretch" spacing={3}>
                      {(clickEventsQuery.data?.items ?? []).map((item) => (
                        <Box
                          key={item.id}
                          borderWidth="1px"
                          borderRadius="md"
                          p={3}
                        >
                          <Text fontSize="xs" color={mutedTextColor}>
                            {new Date(item.clicked_at).toLocaleString()}
                          </Text>
                          <Box mt={2}>
                            {item.lead_id ? (
                              <Button
                                size="sm"
                                variant="link"
                                onClick={() =>
                                  navigate({
                                    to: "/admin/marketing-leads",
                                    search: { leadId: item.lead_id },
                                  })
                                }
                              >
                                {item.recipient_email || item.lead_email || "—"}
                              </Button>
                            ) : (
                              <Text fontWeight="semibold">
                                {item.recipient_email || item.lead_email || "—"}
                              </Text>
                            )}
                          </Box>
                          <Grid templateColumns="1fr" gap={2} mt={3}>
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                URL
                              </Text>
                              <Text fontSize="sm" wordBreak="break-word">
                                {item.target_url}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                IP
                              </Text>
                              <Text fontSize="sm">
                                {item.ip_address || "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={mutedTextColor}>
                                Referrer
                              </Text>
                              <Text fontSize="sm" wordBreak="break-word">
                                {item.referrer || "—"}
                              </Text>
                            </Box>
                          </Grid>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <TableContainer
                      maxH="360px"
                      overflowY="auto"
                      overflowX="auto"
                    >
                      <Table size="sm">
                        <Thead
                          bg={tableHeadBg}
                          position="sticky"
                          top={0}
                          zIndex={1}
                        >
                          <Tr>
                            <Th>Czas</Th>
                            <Th>Email</Th>
                            <Th>URL</Th>
                            <Th>IP</Th>
                            <Th>Referrer</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(clickEventsQuery.data?.items ?? []).map((item) => (
                            <Tr key={item.id}>
                              <Td>
                                {new Date(item.clicked_at).toLocaleString()}
                              </Td>
                              <Td>
                                {item.lead_id ? (
                                  <Button
                                    size="xs"
                                    variant="link"
                                    onClick={() =>
                                      navigate({
                                        to: "/admin/marketing-leads",
                                        search: { leadId: item.lead_id },
                                      })
                                    }
                                  >
                                    {item.recipient_email ||
                                      item.lead_email ||
                                      "—"}
                                  </Button>
                                ) : (
                                  item.recipient_email || item.lead_email || "—"
                                )}
                              </Td>
                              <Td maxW="280px">
                                <Text noOfLines={2}>{item.target_url}</Text>
                              </Td>
                              <Td>{item.ip_address || "—"}</Td>
                              <Td maxW="220px">
                                <Text noOfLines={2}>
                                  {item.referrer || "—"}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </VStack>
            )}
          </Box>
        </Grid>
      </VStack>
    </Container>
  )
}
